import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Star,
  Tag,
  Info,
  ArrowLeft,
  Loader2,
  XCircle,
  CheckCircle2,
  Share2,
  Heart,
  UserCheck,
  Globe,
  Navigation,
  Ticket,
  AlertCircle
} from 'lucide-react';
const BASE_URL=process.env.REACT_APP_API_BASE_URL;
// const BASE_URL=process.env.REACT_APP_API_URL||"https://styles-questions-ver-mat.trycloudflare.com";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch event details
  const fetchEventDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${BASE_URL}/api/v1/public/event/${id}`,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch event details');
      }

      const data = await response.json();
      
      if (data.success) {
        setEvent(data.event);
      }
    } catch (err) {
      setError(err.message || 'Failed to load event details');
      console.error('Error fetching event:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchEventDetails();
    }
  }, [fetchEventDetails, id]);

  // Format date
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  // Format time to 12-hour format
  const formatTime = useCallback((timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }, []);

  // Calculate days until event
  const getDaysUntil = useCallback((dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  // Format age restrictions
  const formatAgeRestrictions = useCallback((age) => {
    if (!age) return null;
    
    const { minAge, maxAge } = age;
    
    // If both are null or empty, no restrictions
    if ((!minAge || minAge === '') && (!maxAge || maxAge === '')) {
      return 'All ages welcome';
    }
    
    // Only minimum age
    if (minAge && (!maxAge || maxAge === '')) {
      return `${minAge}+ years`;
    }
    
    // Only maximum age
    if ((!minAge || minAge === '') && maxAge) {
      return `Under ${maxAge} years`;
    }
    
    // Both min and max
    if (minAge && maxAge) {
      return `${minAge} - ${maxAge} years`;
    }
    
    return 'All ages welcome';
  }, []);

  // Render stars for rating
  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < Math.floor(rating)
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 sm:h-16 sm:w-16 animate-spin text-[#F4A696] mx-auto mb-4" />
          <p className="text-gray-600">Loading event details...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Event</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-lg hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Not Found State
  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-6">The event you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-lg hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const daysUntil = getDaysUntil(event.date);
  const spotsLeft = event.capacity ? event.capacity - event.registeredCount : null;
  const ageRestriction = formatAgeRestrictions(event.age);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Image */}
      <div className="relative h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC] rounded-xl overflow-hidden shadow-md mx-4 sm:mx-6 md:mx-8 mt-6">
        {event.thumbnail ? (
          <img
            src={event.thumbnail}
            alt={event.title}
            className="w-full h-full object-cover rounded-xl"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="h-24 w-24 text-[#F4A696] opacity-30" />
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Back Button */}
        {/* <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all shadow-lg"
        >
          <ArrowLeft className="w-5 h-5 sm:w-6 sm:w-6 text-gray-900" />
        </button> */}

        {/* Event Status Badge */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm ${
            event.status === 'published' 
              ? 'bg-green-500/90 text-white'
              : event.status === 'cancelled'
              ? 'bg-red-500/90 text-white'
              : event.status === 'completed'
              ? 'bg-blue-500/90 text-white'
              : 'bg-gray-500/90 text-white'
          }`}>
            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
          </span>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">
              {event.title}
            </h1>
            {event.shortDescription && (
              <p className="text-white/90 text-sm sm:text-base md:text-lg max-w-3xl">
                {event.shortDescription}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {/* Date Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
                <Calendar className="w-6 h-6 text-[#F4A696] mx-auto mb-2" />
                <p className="text-xs text-gray-500">Date</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>

              {/* Time Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
                <Clock className="w-6 h-6 text-[#F4A696] mx-auto mb-2" />
                <p className="text-xs text-gray-500">Time</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {formatTime(event.time)}
                </p>
              </div>

              {/* Attendees Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
                <Users className="w-6 h-6 text-[#F4A696] mx-auto mb-2" />
                <p className="text-xs text-gray-500">Attendees</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {event.registeredCount}
                  {event.capacity && `/${event.capacity}`}
                </p>
              </div>

              {/* Rating Card */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
                <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2 fill-yellow-400" />
                <p className="text-xs text-gray-500">Rating</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {event.overallRatings.toFixed(1)}
                </p>
              </div>
            </div>

            {/* Days Until Event Alert */}
            {daysUntil > 0 && daysUntil <= 7 && (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-orange-900">Event Starting Soon!</p>
                  <p className="text-sm text-orange-700">
                    Only {daysUntil} {daysUntil === 1 ? 'day' : 'days'} left until this event
                  </p>
                </div>
              </div>
            )}

            {/* Age Restrictions Alert */}
            {event.age && (event.age.minAge || event.age.maxAge) && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-purple-900">Age Restrictions Apply</p>
                  <p className="text-sm text-purple-700">
                    This event is for attendees aged {ageRestriction}
                  </p>
                </div>
              </div>
            )}

            {/* Description Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#F4A696]" />
                <h2 className="text-lg sm:text-xl font-bold text-[#1B3C5C]">About This Event</h2>
              </div>
              {event.description ? (
                <div 
                  className="text-gray-700 text-sm sm:text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: event.description }}
                />
              ) : (
                <p className="text-gray-500 italic">No description available</p>
              )}
            </div>

            {/* What to Expect */}
            {event.whatToExpect && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Ticket className="w-5 h-5 text-[#F4A696]" />
                  <h2 className="text-lg sm:text-xl font-bold text-[#1B3C5C]">What to Expect</h2>
                </div>
                <div 
                  className="text-gray-700 text-sm sm:text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: event.whatToExpect }}
                />
              </div>
            )}

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="w-5 h-5 text-[#F4A696]" />
                  <h2 className="text-lg sm:text-xl font-bold text-[#1B3C5C]">Tags</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {event.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs sm:text-sm font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Ratings Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#F4A696]" />
                <h2 className="text-lg sm:text-xl font-bold text-[#1B3C5C]">Ratings</h2>
              </div>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-4xl font-bold text-[#1B3C5C]">{event.overallRatings.toFixed(1)}</p>
                  <div className="flex gap-1 mt-2">
                    {renderStars(event.overallRatings)}
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = event.ratings[star] || 0;
                    const total = Object.values(event.ratings).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? (count / total) * 100 : 0;
                    
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-6">{star}★</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400 transition-all"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-lg sticky top-6">
              {/* Pricing */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#1B3C5C] mb-4">Ticket Pricing</h3>
                
                {/* Male Pricing */}
                {event.malePrice && event.malePrice.price > 0 && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">Male</span>
                      </div>
                      <div className="text-right">
                        {event.malePrice.discountedPrice > 0 && event.malePrice.discountedPrice < event.malePrice.price ? (
                          <>
                            <p className="text-lg font-bold text-blue-600">
                              {event.currency} {event.malePrice.discountedPrice}
                            </p>
                            <p className="text-xs text-gray-500 line-through">
                              {event.currency} {event.malePrice.price}
                            </p>
                          </>
                        ) : (
                          <p className="text-lg font-bold text-blue-600">
                            {event.currency} {event.malePrice.price}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-blue-700">
                      {event.maleCount} registered
                    </p>
                  </div>
                )}

                {/* Female Pricing */}
                {event.femalePrice && event.femalePrice.price > 0 && (
                  <div className="p-4 bg-pink-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-pink-600" />
                        <span className="font-semibold text-pink-900">Female</span>
                      </div>
                      <div className="text-right">
                        {event.femalePrice.discountedPrice > 0 && event.femalePrice.discountedPrice < event.femalePrice.price ? (
                          <>
                            <p className="text-lg font-bold text-pink-600">
                              {event.currency} {event.femalePrice.discountedPrice}
                            </p>
                            <p className="text-xs text-gray-500 line-through">
                              {event.currency} {event.femalePrice.price}
                            </p>
                          </>
                        ) : (
                          <p className="text-lg font-bold text-pink-600">
                            {event.currency} {event.femalePrice.price}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-pink-700">
                      {event.femaleCount} registered
                    </p>
                  </div>
                )}
              </div>

              {/* Age Restrictions */}
              {event.age && (event.age.minAge || event.age.maxAge) && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-[#1B3C5C] mb-3">Age Requirements</h3>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-purple-900">
                          {ageRestriction}
                        </p>
                        <p className="text-xs text-purple-700 mt-1">
                          Age verification may be required at entry
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Location */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-[#1B3C5C] mb-3">Location</h3>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-5 h-5 text-[#F4A696] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      <p className="font-semibold">{event.location}</p>
                      <p className="text-gray-600">
                        {event.city}, {event.state}
                      </p>
                      <p className="text-gray-600">
                        {event.country} - {event.pincode}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Audience */}
              {event.audience && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-[#1B3C5C] mb-3">Audience</h3>
                  <span className="inline-flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium capitalize">
                    <Globe className="w-4 h-4" />
                    {event.audience}
                  </span>
                </div>
              )}

              {/* Capacity Warning */}
              {spotsLeft !== null && spotsLeft <= 10 && spotsLeft > 0 && (
                <div className="mb-6 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-xs text-orange-800 font-semibold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Only {spotsLeft} spots left!
                  </p>
                </div>
              )}

              {/* CTA Buttons */}
              {/* <div className="space-y-3">
                <button
                  disabled={event.status !== 'published' || (spotsLeft !== null && spotsLeft <= 0)}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Ticket className="w-5 h-5" />
                  {event.status !== 'published' 
                    ? 'Event Not Available'
                    : spotsLeft !== null && spotsLeft <= 0
                    ? 'Sold Out'
                    : 'Book Now'}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button className="py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
                    <Heart className="w-4 h-4" />
                    Save
                  </button>
                  <button className="py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </div> */}

              {/* Organizer Info */}
              {/* {event.organizer && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Organized by</h3>
                  <p className="text-sm text-gray-600">
                    {event.organizer.firstName} {event.organizer.lastName}
                  </p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;
